// src/pages/finance-portal/inventory/ReorderPlanner.js
//
// Scan → Preview → Generate draft POs. Exposes a seasonality multiplier
// and demand lookback tunable.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, FormControl, Grid, IconButton, InputLabel,
    MenuItem, Paper, Select, Skeleton, Slider, Stack, Switch,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
    FormControlLabel,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency, formatQty,
} from './components/inventoryFormatters';

export default function ReorderPlanner() {
    const navigate = useNavigate();
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    const [locations, setLocations] = useState([]);
    const [locationFilter, setLocationFilter] = useState('');
    const [seasonality, setSeasonality] = useState(1.0);
    const [lookback, setLookback] = useState(30);
    const [autoApprove, setAutoApprove] = useState(false);

    const runScan = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = { seasonality, demand_lookback_days: lookback };
            if (locationFilter) params.location_id = locationFilter;
            const res = await axiosInstance.get('/api/finance/inventory/reorder/scan', { params });
            if (res.data?.success) setCandidates(res.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Scan failed');
        } finally {
            setLoading(false);
        }
    }, [locationFilter, seasonality, lookback]);

    useEffect(() => { runScan(); }, [runScan]);

    useEffect(() => {
        axiosInstance.get('/api/finance/inventory/locations').then((r) => setLocations(r.data?.data || [])).catch(() => {});
    }, []);

    const generate = async () => {
        if (candidates.length === 0) return;
        if (!window.confirm(`Generate ${autoApprove ? 'APPROVED' : 'DRAFT'} POs for ${candidates.length} SKU(s)?`)) return;
        setGenerating(true);
        try {
            const res = await axiosInstance.post('/api/finance/inventory/reorder/generate', {
                location_id: locationFilter || null,
                seasonality, demand_lookback_days: lookback,
                auto_approve: autoApprove,
                reason: 'Manual reorder run from Reorder Planner',
            });
            if (res.data?.success) {
                setInfo(`${res.data.count} PO(s) created. Go to Purchase Orders to review.`);
                runScan();
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Generation failed');
        } finally {
            setGenerating(false);
        }
    };

    const totalValue = candidates.reduce((s, c) => s + (c.estimated_cost || 0), 0);
    const vendorGroups = candidates.reduce((m, c) => {
        const key = c.preferred_vendor_id || 'NONE';
        if (!m[key]) m[key] = { name: c.preferred_vendor_name || 'No vendor', items: [], value: 0 };
        m[key].items.push(c);
        m[key].value += c.estimated_cost || 0;
        return m;
    }, {});

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Reorder Planner
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Identify SKUs below reorder point and auto-generate purchase orders
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<SearchIcon />} onClick={runScan}
                        disabled={loading} sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                        Rescan
                    </Button>
                    <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                        disabled={generating || candidates.length === 0}
                        onClick={generate}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                              bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {generating ? 'Generating…' : `Generate POs (${candidates.length})`}
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Location</InputLabel>
                            <Select value={locationFilter} label="Location"
                                onChange={(e) => setLocationFilter(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {locations.map((l) => (
                                    <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#64748b' }}>
                            Seasonality multiplier: <b>{seasonality.toFixed(2)}x</b>
                        </Typography>
                        <Slider value={seasonality} min={0.5} max={2.5} step={0.1}
                            onChange={(e, v) => setSeasonality(v)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField size="small" fullWidth type="number" label="Demand lookback (days)"
                            value={lookback} onChange={(e) => setLookback(Number(e.target.value) || 30)} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} />}
                            label="Auto-approve generated POs (skip approval workflow)"
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                <Chip label={`${candidates.length} SKU${candidates.length === 1 ? '' : 's'} to reorder`}
                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }} />
                <Chip label={`Estimated spend: ${formatCurrency(totalValue)}`}
                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, bgcolor: '#e0f2fe', color: INV_ACCENT_DARK }} />
                <Chip label={`${Object.keys(vendorGroups).length} vendor${Object.keys(vendorGroups).length === 1 ? '' : 's'}`}
                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, bgcolor: '#dbeafe', color: '#1e40af' }} />
            </Stack>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Available</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Reorder Pt</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Safety Stock</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Lead Time</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Daily Demand</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Recommended</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Vendor</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Est. Cost</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? Array.from({ length: 6 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={10}><Skeleton height={32} /></TableCell></TableRow>
                        )) : candidates.length === 0 ? (
                            <TableRow><TableCell colSpan={10} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                All stock is above reorder points — nothing to do.
                            </TableCell></TableRow>
                        ) : candidates.map((c) => (
                            <TableRow key={`${c.product_id}-${c.location_id}`} hover
                                onClick={() => navigate(`/finance-portal/inventory/products/${c.product_id}`)}
                                sx={{ cursor: 'pointer' }}>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                            {c.sku}
                                        </Typography>
                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#64748b' }}>
                                            {c.name}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }}>{c.location_code}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: '#dc2626' }} align="right">
                                    {formatQty(c.available)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(c.reorder_point)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(c.safety_stock)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{c.lead_time_days}d</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(c.avg_daily_demand, 3)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }} align="right">
                                    <Chip size="small" label={formatQty(c.recommended_qty)} color="primary"
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 700 }} />
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>
                                    {c.preferred_vendor_name || (
                                        <Chip size="small" label="No vendor" color="warning" />
                                    )}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                    {formatCurrency(c.estimated_cost, c.currency)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
